import { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2, LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useForgotPasswordEmail, useResetViaEmail } from '@/api/hooks/useAuthExtra';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Step = 'request' | 'reset' | 'done';

/**
 * Email-code reset: request a code, then redeem it for a new password.
 * (The security-question path is a server-side alternative; this covers the
 * flow LoginPage's "Forgot password?" link actually needs.)
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const sendCode = useForgotPasswordEmail();
  const resetPassword = useResetViaEmail();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSendCode(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await sendCode.mutateAsync(email.trim());
      setStep('reset');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  async function handleReset(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await resetPassword.mutateAsync({ email: email.trim(), code, newPassword });
      setStep('done');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-grad-gold font-display text-xl font-bold text-ink-950">
            F
          </div>
          <span className="font-display text-xl tracking-tight">{t('appName')}</span>
        </div>

        {step === 'done' ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-4 font-display text-2xl">Password reset</h1>
            <p className="mt-2 text-sm text-muted-foreground">You can log in with your new password now.</p>
            <Button className="mt-6 w-full" asChild>
              <Link to="/login">Go to login</Link>
            </Button>
          </div>
        ) : step === 'request' ? (
          <>
            <h1 className="font-display text-3xl">Forgot password</h1>
            <p className="mt-1 text-sm text-muted-foreground">We'll email you a reset code.</p>

            <form onSubmit={handleSendCode} className="mt-8 space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {error && (
                <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
                  {error}
                </p>
              )}

              <Button type="submit" size="touch" className="w-full" disabled={sendCode.isPending}>
                {sendCode.isPending && <LoaderCircle className="animate-spin" />}
                Send reset code
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl">Reset password</h1>
            <p className="mt-1 text-sm text-muted-foreground">Enter the code sent to {email}.</p>

            <form onSubmit={handleReset} className="mt-8 space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="code">Reset code</Label>
                <Input id="code" required value={code} onChange={(e) => setCode(e.target.value)} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="newPassword">New password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                />
              </div>

              {error && (
                <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
                  {error}
                </p>
              )}

              <Button type="submit" size="touch" className="w-full" disabled={resetPassword.isPending}>
                {resetPassword.isPending && <LoaderCircle className="animate-spin" />}
                Reset password
              </Button>
            </form>
          </>
        )}

        <p className="mt-8 text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-primary hover:underline">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
